import { useId } from "react";

import {
  EVERY_MINUTES,
  nextRuns,
  runTimeWords,
  scheduleWords,
  type Preset,
} from "@/lib/integrations/schedule";
import type { Settings } from "@/lib/integrations/settings";

import { CheckboxField, Field, FieldRow, Input, Segmented, Select } from "./controls";
import styles from "./integrations.module.css";

type Kind = "every" | "hourly" | "daily" | "manual";

/** When Scout runs the job: presets, the time in words in the event's zone, and the next three runs. */
export function SchedulePicker({
  preset,
  timeZone,
  onChange,
  now = new Date(),
}: {
  preset: Preset;
  timeZone: string;
  onChange: (preset: Preset) => void;
  now?: Date;
}) {
  const minutesId = useId();
  const timeId = useId();
  const kind: Kind = preset.kind === "custom" ? "manual" : preset.kind;
  const next = nextRuns(preset, timeZone, now);
  return (
    <div style={{ display: "grid", gap: "var(--s3)" }}>
      {preset.kind === "custom" && (
        <p className={styles.muted}>
          Scout has its own timing for this job ({preset.cron}). Pick a preset to replace it.
        </p>
      )}
      <Segmented<Kind>
        name="How often"
        value={kind}
        options={[
          { value: "every", label: "Every few minutes" },
          { value: "hourly", label: "Every hour" },
          { value: "daily", label: "Daily at a time" },
          { value: "manual", label: "Only by hand" },
        ]}
        onChange={(value) => {
          if (value === "every") onChange({ kind: "every", minutes: 10 });
          else if (value === "hourly") onChange({ kind: "hourly" });
          else if (value === "daily") onChange({ kind: "daily", time: "07:00" });
          else onChange({ kind: "manual" });
        }}
      />
      {preset.kind === "every" && (
        <FieldRow>
          <Field label="Every" htmlFor={minutesId}>
            <Select
              id={minutesId}
              value={preset.minutes}
              onChange={(event) => onChange({ kind: "every", minutes: Number(event.target.value) })}
            >
              {EVERY_MINUTES.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes} minutes
                </option>
              ))}
            </Select>
          </Field>
        </FieldRow>
      )}
      {preset.kind === "daily" && (
        <FieldRow>
          <Field label={`Time (${timeZone})`} htmlFor={timeId}>
            <Input
              id={timeId}
              type="time"
              value={preset.time}
              onChange={(event) => onChange({ kind: "daily", time: event.target.value || "00:00" })}
            />
          </Field>
        </FieldRow>
      )}
      <p style={{ margin: 0 }}>
        <strong>{scheduleWords(preset, timeZone)}</strong>
      </p>
      {next.length > 0 && (
        <p className={styles.muted} style={{ margin: 0 }}>
          Next runs: {next.map((date) => runTimeWords(date, timeZone)).join(" · ")} ({timeZone} time)
        </p>
      )}
    </div>
  );
}

function asPercent(share: number): number {
  return Math.round(share * 100);
}

/** Each guard with its setting and what happens if it trips. */
export function GuardList({
  safety,
  onChange,
  disabled = false,
}: {
  safety: Settings["safety"];
  onChange: (safety: Settings["safety"]) => void;
  disabled?: boolean;
}) {
  const fewId = useId();
  const placedId = useId();
  const refusedId = useId();
  const fromId = useId();
  const untilId = useId();
  const set = (patch: Partial<Settings["safety"]>) => onChange({ ...safety, ...patch });
  const share = (text: string) => Math.max(0, Math.min(100, Number(text) || 0)) / 100;
  const windowOn = Boolean(safety.writeFrom || safety.writeUntil);
  return (
    <div style={{ display: "grid", gap: "var(--s4)" }}>
      <div>
        <FieldRow>
          <CheckboxField
            label="Far fewer rows than usual"
            checked={safety.minShare > 0}
            disabled={disabled}
            onChange={(event) => set({ minShare: event.target.checked ? 0.5 : 0 })}
          />
          <Field label="Pause when a run has fewer than this share of the usual rows (%)" htmlFor={fewId}>
            <Input
              id={fewId}
              type="number"
              min={0}
              max={100}
              disabled={disabled || safety.minShare === 0}
              value={asPercent(safety.minShare)}
              onChange={(event) => set({ minShare: share(event.target.value) })}
            />
          </Field>
        </FieldRow>
        <p className={styles.faint}>If it trips: nothing is written, it pauses and asks “Write anyway?”. Usual is the average of the last 5 written runs.</p>
      </div>
      <div>
        <FieldRow>
          <CheckboxField
            label="Too many rows not placed or held"
            checked={safety.maxNotPlaced < 1}
            disabled={disabled}
            onChange={(event) => set({ maxNotPlaced: event.target.checked ? 0.1 : 1 })}
          />
          <Field label="Limit (%)" htmlFor={placedId}>
            <Input
              id={placedId}
              type="number"
              min={0}
              max={100}
              disabled={disabled || safety.maxNotPlaced >= 1}
              value={asPercent(safety.maxNotPlaced)}
              onChange={(event) => set({ maxNotPlaced: share(event.target.value) })}
            />
          </Field>
        </FieldRow>
        <p className={styles.faint}>If it trips: nothing from the run is written, and it pauses with the list.</p>
      </div>
      <div>
        <FieldRow>
          <CheckboxField
            label="Too many rows refused by rules"
            checked={safety.maxRefused < 1}
            disabled={disabled}
            onChange={(event) => set({ maxRefused: event.target.checked ? 0.1 : 1 })}
          />
          <Field label="Limit (%)" htmlFor={refusedId}>
            <Input
              id={refusedId}
              type="number"
              min={0}
              max={100}
              disabled={disabled || safety.maxRefused >= 1}
              value={asPercent(safety.maxRefused)}
              onChange={(event) => set({ maxRefused: share(event.target.value) })}
            />
          </Field>
        </FieldRow>
        <p className={styles.faint}>If it trips: the whole run is taken back, and it pauses with the refused rows and the rules’ sentences.</p>
      </div>
      <div>
        <FieldRow>
          <CheckboxField
            label="Only write during these dates"
            checked={windowOn}
            disabled={disabled}
            onChange={(event) =>
              set(event.target.checked ? { writeFrom: safety.writeFrom ?? "", writeUntil: safety.writeUntil ?? "" } : { writeFrom: null, writeUntil: null })
            }
          />
          <Field label="From" htmlFor={fromId}>
            <Input
              id={fromId}
              type="date"
              disabled={disabled || !windowOn}
              value={safety.writeFrom ?? ""}
              onChange={(event) => set({ writeFrom: event.target.value || null })}
            />
          </Field>
          <Field label="Until" htmlFor={untilId}>
            <Input
              id={untilId}
              type="date"
              disabled={disabled || !windowOn}
              value={safety.writeUntil ?? ""}
              onChange={(event) => set({ writeUntil: event.target.value || null })}
            />
          </Field>
        </FieldRow>
        <p className={styles.faint}>Dates are read in the event’s time zone. omnium never deletes a record because a row is missing.</p>
      </div>
    </div>
  );
}
